// render-player-card.ts — instant TS render of the PLAYER CHARACTER SHEET CARD from casterCache.
//
// window.ftRenderPlayerCardFromCache(casterId, container, opts) paints the full tabbed PC sheet
// (the table_item_player_character_inc.php shell) with ZERO server round-trip: the blob is already
// client-side (encounterCache alias seed → casterCache), every sheet tab is a TS pane mount, and
// the shell (summary mount / nav pills / core-abilities banner / class-mechanic bars) is ported
// here. Returns true on a clean render; false on ANY gate below so template.js falls back to the
// server queryData() path unchanged. Plan: plans/pc-sheet-full-ts-migration-session.md §4 item 2+3.
//
// SERVER-FALLBACK GATES (cases this v1 deliberately does NOT render):
//   - cache miss / encounter_creatures.item_type !== 'player_character' (the PC caster ROOT has no
//     items bucket and no item_type — the type lives ONLY on the encounter_creatures bucket)
//   - viewer lacks player+campaign permission, unless an encounter surface explicitly requests a
//     read-only inspection of a PC present in that viewer's redacted encounter projection (the PHP
//     path still covers standalone cards and non-permission LOOTERS of a dead/dying PC)
// Everything else renders from the blob; the only AJAX is LAZY, on first tab activation, for the
// owner-blessed edge cases: summons strip + encounter explorable-links (and the panes' own
// Pattern-B fetches: prepare-spells table, crafting catalog).
//
// The card root carries data-ft-pc-owns-sheet="1" + data-ft-caster-id (same seam as the PHP shell),
// so applyCasterSideEffects skips the legacy full-AJAX refreshCasterSheet — the TS panes + summary
// repaint surgically on caster_update / caster_update_delta / encounter re-alias.
//
// PHP sources ported here (the includes stay live for the server path):
//   options/tables/table_item_player_character_inc.php           (shell: root + pills + panes)
//   options/tables/creature/hidden_inputs_for_refresh_inc.php
//   options/game/character_sheet_display/abilities/_abilities_inc.php        (wrapper + search)
//   options/game/character_sheet_display/abilities/core_abilities_inc.php    (PC/main branch)
//   options/game/character_sheet_display/abilities/class_mechanics_inc.php   (UUID→variable map)
//   options/game/character_sheet_display/abilities/class_mechanic_bar_inc.php
//   options/game/character_sheet_display/abilities/shaman_motes_bar_inc.php
//   options/game/character_sheet_display/summons/_summons_inc.php → blob-first pill strip from
//     the baked encounter_creatures.caster_summons list (sub-sheets stay lazy; the legacy
//     summons_tab_ajax_inc.php endpoint survives ONLY as the stale-pre-bake-blob fallback)
//   options/game/character_sheet_display/encounter/_encounter_inc.php → blob-first body (name /
//     picture / description from encounterCache); ONLY the explorable-links block lazy-fetches
//     via encounter_tab_ajax_inc.php?links_only=1 (server graph walk, viewer-permission-bound)
// Weapons tab wrapper reused from render-creature-card.ts (same PHP include both paths).

import { paintMarkup } from '../player-character-sheet/paint-markup';
import {
    itemsCol, layerOf, layerHas, layerEntriesOf, layerListEntryUuid,
    computedValue, encounterCreatureField, encounterField, pictureListOf, itemVideos,
    hasPlayerPermission, hasCampaignPermission,
} from '../shared/array-types';
import {
    AB_SPOT_HIDDEN, AB_SEARCH_HIDDEN_INV, AB_RESOLVE, AB_SNAP_OUTTA_IT, AB_STOP_BLEEDING,
    AB_CUSTOM_FULL, AB_CUSTOM, AB_CUSTOM_QUICK, AB_CUSTOM_FREE, AB_CUSTOM_REACTION,
    ST_BLEEDING, ST_MODERATE_BLEEDING, ST_SEVERE_BLEEDING,
    esc, iconPh, cookieVal, hexOf, bucketScalar, hasStatus, triggerIsActive, uuidOk, paneMount,
    ptMount, ABILITIES_TAB_STYLE, buildCarousel,
} from './card-shell';
import { buildWeaponsTab, buildPassiveAbilitiesSearchWrap, hasMentaBucket } from './render-creature-card';
import { findFullNestedItem } from './find-cached-item';
import type { CachedItemNode } from './find-cached-item';
import { setOpenSideInfo } from './open-side-info';
import { holdPendingEntityCard, type EntityCardPaintHooks } from './entity-card-state';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';

type PlayerBlob = CachedItemNode;

type PlayerCardWindow = Window & {
    _ft_user_id?: unknown;
    handleAbilityBtnSingleClick?: (abilityId: string) => unknown;
    ftDispatchAbilityClick?: (casterId: string, abilityId: string) => boolean;
    ftLegacyAbilityClickAs?: (casterId: string, abilityId: string) => void;
    ftTriggeredAbilityDebouncedPrimaryClick?: (event: MouseEvent, element: HTMLElement) => unknown;
    toggleTriggerActive?: (abilityId: string, casterId: string, encounterId: string, active: number) => unknown;
    clearTriggersFromCoreAbilitiesButton?: (element: HTMLElement) => unknown;
    addAPToCreature?: () => unknown;
    addReactionToCreature?: () => unknown;
    adjustShamanMoteToggle?: (abilityId: string, current: number, max: number, element: HTMLElement) => unknown;
    setCasterSummon?: (summonId: string) => unknown;
    setCookie?: (name: string, value: string, days: number) => unknown;
    clickActiveCasterSummonsTab?: () => unknown;
    ftAssertSelfCasterOnTab?: (casterId: string) => unknown;
    changeCharges?: (abilityId: string, inputId: string) => unknown;
    ftEnsureSummonSheetLoaded?: (summonId: string) => unknown;
    ftGetActiveCasterId?: () => unknown;
    ftGetCampaignId?: () => unknown;
    ftMountAbilityViewToolbars?: () => void;
    ftMountEncounterMinimaps?: (root?: Element | Document) => void;
    ftMountIcons?: (root?: Element | Document) => void;
    ftMountInventoryTiles?: (root?: Element | Document) => void;
    ftMountParsedText?: (root?: Element | Document) => void;
    ftMountParseTextTables?: (root?: Element | Document) => void;
    ftMountPcSheet?: (root?: ParentNode) => void;
    ftMountRuneIcons?: (root?: Element | Document) => void;
    ftMountThumbnails?: (root?: Element | Document) => void;
    ftSeedCasterFromEncounterCache?: (casterId: string) => boolean;
    ftSetActiveCaster?: (casterId: string) => unknown;
    ftSetSelectedSummon?: (summonId: string) => unknown;
    queryData?: (url: string, targetId: string, data: Record<string, string>) => unknown;
    refreshDragAndDrop?: () => void;
    renderCasterSpecialActionsInline?: (casterId: string, targetId: string, sideInfo: boolean) => void;
};

function recordOf(value: unknown): PlayerBlob | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as PlayerBlob
        : null;
}

function playerWindow(): PlayerCardWindow {
    return window as PlayerCardWindow;
}

function computedCreature(blob: PlayerBlob): PlayerBlob | null {
    return recordOf(recordOf(blob.computed)?.creature);
}

function scalarCharge(value: unknown): number | string | null {
    return typeof value === 'number' || typeof value === 'string' ? value : null;
}

function pcCardAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-pc-card-action="${esc(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${esc(value)}"`);
    }
    return pairs.join(' ');
}

function pcCardAbilityAttrs(abilityId: string): string {
    return pcCardAttrs('ability-click', { abilityId });
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-pc-card-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftPcCardAction ?? '';
    const w = window as PlayerCardWindow;
    if (action === 'ability-click') {
        const abilityId = el.dataset.ftAbilityId ?? '';
        if (abilityId !== '') {
            event.preventDefault();
            const casterId = el.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId ?? '';
            if (w.ftDispatchAbilityClick?.(casterId, abilityId)) return;
            // Cast AS this card's creature, not whichever one is active (action-bar.ts ftLegacyAbilityClickAs).
            if (w.ftLegacyAbilityClickAs) w.ftLegacyAbilityClickAs(casterId, abilityId);
            else w.handleAbilityBtnSingleClick?.(abilityId);
        }
    } else if (action === 'triggered-primary') {
        event.preventDefault();
        w.ftTriggeredAbilityDebouncedPrimaryClick?.(event, el);
    } else if (action === 'toggle-trigger') {
        event.preventDefault();
        w.toggleTriggerActive?.(
            el.dataset.ftAbilityId ?? '',
            el.dataset.ftCasterId ?? '',
            el.dataset.ftEncounterId ?? '',
            Number(el.dataset.ftActive ?? '0'),
        );
    } else if (action === 'clear-triggers') {
        event.preventDefault();
        w.clearTriggersFromCoreAbilitiesButton?.(el);
    } else if (action === 'add-ap') {
        event.preventDefault();
        w.addAPToCreature?.();
    } else if (action === 'add-reaction') {
        event.preventDefault();
        w.addReactionToCreature?.();
    } else if (action === 'shaman-mote-toggle') {
        event.preventDefault();
        w.adjustShamanMoteToggle?.(el.dataset.ftAbilityId ?? '', Number(el.dataset.ftCurrent ?? '0'), Number(el.dataset.ftMax ?? '1'), el);
    } else if (action === 'set-summon') {
        const summonId = el.dataset.ftSummonId ?? '';
        if (summonId !== '') w.setCasterSummon?.(summonId);
    } else if (action === 'tab-click') {
        const tabKey = el.dataset.ftTabKey ?? '';
        const casterId = el.dataset.ftCasterId ?? '';
        if (tabKey !== '') w.setCookie?.('cookie_player_character_sheet_menu_tab', `tab_${tabKey}`, 7);
        if (tabKey === 'summons') w.clickActiveCasterSummonsTab?.();
        else if (casterId !== '') w.ftAssertSelfCasterOnTab?.(casterId);
    }
});

document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-pc-card-action="shaman-mote-toggle"]')
        : null;
    if (!el || !document.contains(el)) return;
    event.preventDefault();
    (window as PlayerCardWindow).adjustShamanMoteToggle?.(el.dataset.ftAbilityId ?? '', Number(el.dataset.ftCurrent ?? '0'), Number(el.dataset.ftMax ?? '1'), el);
});

document.addEventListener('input', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLInputElement>('[data-ft-pc-card-action="mechanic-slider"]')
        : null;
    if (!el || !document.contains(el)) return;
    const label = document.getElementById(el.dataset.ftLabelId ?? '');
    if (label) label.textContent = `${parseInt(el.value, 10) || 0} / ${el.dataset.ftMax ?? '0'}`;
});

document.addEventListener('change', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLInputElement>('[data-ft-pc-card-action="mechanic-slider"]')
        : null;
    if (!el || !document.contains(el)) return;
    const abilityId = el.dataset.ftAbilityId ?? '';
    const inputId = el.id;
    if (abilityId !== '' && inputId !== '') (window as PlayerCardWindow).changeCharges?.(abilityId, inputId);
});

// ── PC-only catalog constants (stable UUIDs, same values the PHP catalogs resolve) ───────────────
const AB_SHORT_REST = '4744CC19-87A9-41D7-9285-ABD36470EC81';
const AB_REST = '5B363C9C-3207-4C45-A863-5506DDC06116';
const CASTER_TYPE_FULL = 'A18765B6-F35A-47E2-F112-DF1582C37B37';
const CASTER_TYPE_HALF = 'E8FB3E1F-2B16-4889-831C-99FC9E34853E';
// PlayableClass 'shaman' (additions.php). Used to gate the elemental-motes bar via class
// membership — the old `shaman_class_level` scratch layer is DEAD output, so layer_has() never
// matched and the bar never rendered. See caster_has_playable_class() (PHP twin).
const CLASS_SHAMAN_ID = '90474F60-446A-A55D-9B1F-9F974AD13D87';
// ClassMechanic::SHAMAN_MOTES — four passive_ability rows, charges per mote.
const SHAMAN_MOTES: ReadonlyArray<{ abilityId: string; icon: string }> = [
    { abilityId: '3461E6F1-3EFF-8697-DB10-D4FEACF6679E', icon: 'air_mote' },
    { abilityId: '3F3CB315-7406-30C9-D093-6BA50EF91739', icon: 'fire_mote' },
    { abilityId: 'F6D275CB-ACF3-DAC3-7236-810BC03A8298', icon: 'water_mote' },
    { abilityId: '0B345CE4-611B-E8BA-F4A2-B15A43A0B193', icon: 'earth_mote' },
];
// class_mechanics_inc.php UUID → bar variable name (icon key + label). The variable names follow
// the include's hardcoded strings (rites/gifts), NOT the ClassMechanic catalog (rite/gift).
const MENACE_ID = 'A8084E61-46B9-FDB5-83CD-59482BA5B8D3';
const CLASS_MECHANIC_VARS: Record<string, string> = {
    'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6': 'fury',      // barbarian
    '59F12E46-1F98-1352-1A13-BE202196C347': 'melody',    // bard
    'BD233002-F6C0-A136-6EE4-C899A9E92856': 'tactics',   // champion
    [MENACE_ID]: 'menace',                               // deathbringer
    'C92D2BD6-DE2F-2A4D-7423-0C40CC8B3305': 'harmony',   // druid
    '8C9782A8-5EBF-C04F-CA3D-DF89B1C9EA18': 'stalk',     // hunter
    '7926555F-184A-4E33-9630-A845D4C9335C': 'rites',     // priest
    '6A8183E1-349E-3960-B465-6C863097E193': 'psi',       // psion
    '2274AD74-E2F8-47AA-8FD1-7DE4713695B9': 'sanguine',  // sorcerer
    '26A48978-7629-F0D3-BA6F-23DEBB429055': 'cunning',   // thief
    '30BD0A8F-DC53-4B80-B5C4-8FBB04F9F9C1': 'gifts',     // warlock
    'B56929A7-6BB8-81FA-BFF8-2CE4B0812030': 'mastery',   // wizard
    '5FF2F49A-7667-D686-2BC6-32623375A091': 'aether',    // spellblade
    '7A725B38-BA68-7651-4746-5D34B5690203': 'ether',     // summoner
};

// ── ability-entry twins (mirrors of ability-row.ts readers — that module lives in the
//    player-character-sheet bundle, which this bundle cannot import) ──────────────────────────────
function abilityNest(entry: unknown): unknown {
    const row = recordOf(entry);
    if (row) {
        const ia = row.item_arr
            ?? (!('item_arr' in row) && typeof row.item_id === 'string' ? row.item_id : undefined);
        if (recordOf(ia)) return ia;
        if (typeof ia === 'string' && ia !== '') {
            return findFullNestedItem(ia) ?? { items: { item_id: ia } };
        }
    }
    return entry;
}
function abilityEntryId(entry: unknown): string {
    if (typeof entry === 'string') return entry;
    const row = recordOf(entry);
    if (!row) return '';
    if (typeof row.ability_id === 'string' && row.ability_id !== '') return row.ability_id;
    const id = itemsCol(abilityNest(entry), 'item_id');
    return typeof id === 'string' ? id : '';
}
/** passive_ability_entry_is_active() twin — key absent (bare ref / unstamped) = active. */
function passiveEntryActive(entry: unknown): boolean {
    if (typeof entry === 'string') return entry !== '';
    const row = recordOf(entry);
    if (!row) return false;
    if (!('passive_conditions_active' in row)) return true;
    return !!row.passive_conditions_active;
}
function entryCharges(entry: unknown): number | string | null {
    const row = recordOf(entry);
    if (!row) return null;
    const charges = recordOf(row.charges);
    if (charges?.current !== undefined) return scalarCharge(charges.current);
    if (row.ability_charges !== undefined) return scalarCharge(row.ability_charges);
    const nested = recordOf(row.item_arr)?.ability_charges;
    if (Array.isArray(nested) && nested[0] !== undefined) return scalarCharge(nested[0]);
    const layered = layerOf(abilityNest(entry), 'ability_charges');
    return (layered === null || layered === undefined || layered === '') ? null : scalarCharge(layered);
}
function entryMaxCharges(entry: unknown): number | string | null {
    const row = recordOf(entry);
    if (!row) return null;
    const charges = recordOf(row.charges);
    if (charges?.max !== undefined) return scalarCharge(charges.max);
    if (row.max_ability_charges !== undefined) return scalarCharge(row.max_ability_charges);
    const nested = recordOf(row.item_arr)?.ability_charges;
    if (Array.isArray(nested) && nested[0] !== undefined) return scalarCharge(nested[0]);
    const layered = layerOf(abilityNest(entry), 'ability_charges');
    return (layered === null || layered === undefined || layered === '') ? null : scalarCharge(layered);
}

/** PHP layer_entries() bridge twin for ability-type layers: the COMPILED list
 *  (computed.creature.abilities.<type>, where the live charge/active state lives) merged with the
 *  carrier walk, deduped by ability id, suppression-filtered. Mirrors abilityEntriesOfType
 *  (ability-row.ts) minus the level gate — the charge readers here only consult compiled entries
 *  that the build already level-gated. */
function abilityEntriesOf(blob: PlayerBlob, type: string): unknown[] {
    const out: unknown[] = [];
    const seen: Record<string, true> = {};
    const push = (list: unknown): void => {
        const arr = Array.isArray(list) ? list : (list !== null && list !== undefined && list !== '' ? [list] : []);
        for (const e of arr) {
            const idk = abilityEntryId(e);
            if (idk && seen[idk]) continue;
            if (idk) seen[idk] = true;
            out.push(e);
        }
    };
    const creature = computedCreature(blob);
    push(recordOf(creature?.abilities)?.[type]);
    push(recordOf(creature?.skill_ability)?.[type]);
    push(layerEntriesOf(blob, type));
    const suppressed = recordOf(creature?.suppressed_ability_ids);
    if (!suppressed) return out;
    return out.filter((e) => {
        const aid = abilityEntryId(e);
        return !(aid !== '' && suppressed[aid]);
    });
}

/** get_ability_charges_by_ability_id() twin against a layer's entries. */
function abilityChargesById(blob: PlayerBlob, layerName: string, abilityId: string): string {
    for (const entry of abilityEntriesOf(blob, layerName)) {
        const row = recordOf(entry);
        if (!row) continue;
        if (abilityEntryId(entry) !== abilityId) continue;
        if (row.passive_conditions_active === false) continue;
        const c = entryCharges(entry);
        if (c !== null) return String(c);
    }
    return '';
}

function ucfirst(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
function sanId(id: string): string { return String(id).replace(/[^A-Za-z0-9_]/g, ''); }

/** caster_has_playable_class() twin: scans player_characters.class_1..20 for the given class
 *  UUID. Slot may be a bare-UUID scalar (current blob shape) or a full provider nest / [0]-wrap. */
function casterHasPlayableClass(blob: PlayerBlob, classId: string): boolean {
    const pc = recordOf(blob.player_characters);
    if (!pc) return false;
    const want = classId.toUpperCase();
    for (let x = 1; x <= 20; x++) {
        const slot = pc['class_' + x];
        if (slot === undefined || slot === null || slot === '') continue;
        const cid = typeof slot === 'string'
            ? slot
            : String(itemsCol(slot, 'item_id') ?? (Array.isArray(slot) ? slot[0] : '') ?? '');
        if (uuidOk(cid) && cid.toUpperCase() === want) return true;
    }
    return false;
}

/** ft_campaign_context() presence twin: PC attached_campaign_id, else the page campaign input
 *  (covers the encounter-master-parent resolution when opened on a campaign page). */
function campaignIdOf(blob: PlayerBlob): string {
    const attached = bucketScalar(recordOf(blob.player_characters) ?? {}, 'attached_campaign_id');
    if (uuidOk(attached)) return attached;
    const page = String(playerWindow().ftGetCampaignId?.() || '');
    return uuidOk(page) ? page : '';
}

// ── CLASS MECHANIC BARS (port of class_mechanics_inc.php + class_mechanic_bar_inc.php) ───────────
function buildClassMechanicBar(blob: PlayerBlob, abilityId: string, varName: string, sideInfo: boolean, canEdit: boolean): string {
    let html = '';
    const gaps = sideInfo ? ' gap-3' : '';
    for (const entry of abilityEntriesOf(blob, 'passive_ability')) {
        if (abilityEntryId(entry) !== abilityId || !passiveEntryActive(entry)) continue;
        const curRaw = entryCharges(entry);
        const maxRaw = entryMaxCharges(entry);
        // 'NA' / unresolved variable tags coerce to 0 (mirrors the PHP is_numeric guard).
        const cur = (curRaw !== null && curRaw !== '' && !isNaN(Number(curRaw))) ? Number(curRaw) : 0;
        const max = (maxRaw !== null && maxRaw !== '' && !isNaN(Number(maxRaw))) ? Number(maxRaw) : 0;
        const pct = max > 0 ? Math.round((cur / max) * 100) : 0;
        const label = esc(ucfirst(varName));

        if (!canEdit) {
            html += `<div class="d-flex justify-content-center align-items-center p-3 m-3 border rounded ft-class-mechanic-bar--view-only ft-class-mechanic-bar${gaps}">
                <div class="col-1 flex-shrink-0">${iconPh(varName)}</div>
                <div class="flex-grow-1 min-w-0">
                    <div class="progress border" role="progressbar" aria-label="${label}" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar bg-warning text-dark" style="width: ${pct}%">${cur} / ${max}</div>
                    </div>
                </div>
            </div>`;
            continue;
        }

        const ctx = sideInfo ? '_side' : '';
        const sliderId = `${abilityId}_class_mechanic_bar_slider${ctx}`;
        const labelId = `${abilityId}_class_mechanic_bar_label${ctx}`;
        const inner = max <= 0
            ? `<div class="flex-grow-1 min-w-0">
                    <div class="progress border" role="progressbar" aria-label="${label}" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar bg-warning text-dark" style="width: 0%">${cur} / ${max}</div>
                    </div>
                </div>`
            : `<div class="flex-grow-1 d-flex align-items-center gap-2 min-w-0 px-2">
                    <input type="range" id="${esc(sliderId)}" class="form-range flex-grow-1" min="0" max="${max}" step="1" value="${cur}"
                        aria-label="Set ${label}"
                        ${pcCardAttrs('mechanic-slider', { abilityId, labelId, max: String(max) })}>
                    <span id="${esc(labelId)}" class="small text-nowrap">${cur} / ${max}</span>
                </div>`;
        html += `<div class="d-flex justify-content-center align-items-center p-3 m-3 border rounded ft-class-mechanic-bar${gaps}">
            <div class="col-1 flex-shrink-0">${iconPh(varName)}</div>
            ${inner}
        </div>`;
    }
    return html;
}

export function buildClassMechanicBars(blob: PlayerBlob, sideInfo: boolean, canEdit: boolean): string {
    if (!layerHas(blob, 'passive_ability')) return '';
    const activeIds = new Set<string>();
    for (const pa of abilityEntriesOf(blob, 'passive_ability')) {
        if (!passiveEntryActive(pa)) continue;
        const id = abilityEntryId(pa);
        if (uuidOk(id)) activeIds.add(id.toUpperCase());
    }
    // A bar renders for every ACTIVE mapped mechanic passive. The PHP gates on "listed on the
    // merged class_mechanic layer" — but that merge is first-carrier-wins, so a MULTICLASS PC's
    // second mechanic can drop off it (the include grew a Deathbringer-Menace fallback for exactly
    // that miss). Since a mechanic passive is only ever granted by its own class, active-passive
    // membership IS the real condition; listed order first for PHP-parity bar ordering.
    const ordered: string[] = [];
    for (const entry of layerEntriesOf(blob, 'class_mechanic')) {
        const uuid = String(layerListEntryUuid(entry) ?? '').toUpperCase();
        if (uuidOk(uuid) && !ordered.includes(uuid)) ordered.push(uuid);
    }
    for (const uuid of Object.keys(CLASS_MECHANIC_VARS)) {
        if (!ordered.includes(uuid)) ordered.push(uuid);
    }
    let html = '';
    for (const uuid of ordered) {
        const varName = CLASS_MECHANIC_VARS[uuid];
        if (!varName || !activeIds.has(uuid)) continue;
        html += buildClassMechanicBar(blob, uuid, varName, sideInfo, canEdit);
    }
    // Shaman elemental motes (port of shaman_motes_bar_inc.php; same gate as class_mechanics_inc).
    // Gate on class membership: the `shaman_class_level` scratch layer is DEAD output (stripped in
    // query_player_character_arr_inc.php), so the old layerHas() check never fired and the motes bar
    // never rendered for any shaman. casterHasPlayableClass() reads player_characters.class_1..20.
    // NOTE: this port reads the compiled passive entries directly (the include's intent), unlike the
    // PHP guard on the legacy top-level $caster_arr['passive_ability'] slot (gone post-reshape).
    if (casterHasPlayableClass(blob, CLASS_SHAMAN_ID)) {
        html += buildShamanMotesBar(blob, canEdit);
    }
    return html;
}

// ── SHAMAN MOTES BAR (port of shaman_motes_bar_inc.php) ──────────────────────────────────────────
function buildShamanMotesBar(blob: PlayerBlob, canEdit: boolean): string {
    const byId = new Map<string, unknown>();
    for (const row of abilityEntriesOf(blob, 'passive_ability')) {
        const id = abilityEntryId(row);
        if (!uuidOk(id) || !passiveEntryActive(row)) continue;
        byId.set(id.toUpperCase(), row);
    }
    let slots = '';
    for (const mote of SHAMAN_MOTES) {
        const row = byId.get(mote.abilityId) ?? null;
        const curRaw = row !== null ? entryCharges(row) : null;
        const maxRaw = row !== null ? entryMaxCharges(row) : null;
        const current = (curRaw !== null && curRaw !== '' && !isNaN(Number(curRaw))) ? Math.trunc(Number(curRaw)) : 0;
        let max = (maxRaw !== null && maxRaw !== '' && !isNaN(Number(maxRaw))) ? Math.trunc(Number(maxRaw)) : 1;
        if (max < 1) max = 1;
        const active = current > 0;
        const interactive = row !== null;
        const canToggle = interactive && canEdit;

        let slotClass = 'ft-shaman-mote d-flex flex-column align-items-center justify-content-center p-2 rounded border';
        slotClass += active ? ' ft-shaman-mote--active' : ' ft-shaman-mote--empty';
        if (!interactive) slotClass += ' ft-shaman-mote--locked';
        if (interactive && !canEdit) slotClass += ' ft-shaman-mote--view-only';

        const title = !interactive ? 'Mote not available' : (!canEdit ? 'Mote (view only)' : 'Toggle mote charges');
        const interaction = canToggle
            ? `role="button" tabindex="0" ${pcCardAttrs('shaman-mote-toggle', { abilityId: mote.abilityId, current: String(current), max: String(max) })}`
            : 'aria-disabled="true"';

        slots += `<div class="${esc(slotClass)}" ${interaction} title="${title}">
            <span class="ft-shaman-mote-icon">${iconPh(mote.icon)}</span>
            ${max > 1 ? `<span class="small text-muted ft-shaman-mote-count">${current} / ${max}</span>` : ''}
        </div>`;
    }
    return `<div class="d-flex justify-content-center align-items-stretch flex-wrap gap-2 p-3 m-3 border rounded ft-shaman-motes-bar"
        role="group" aria-label="Shaman motes">${slots}</div>`;
}

// ── CORE ABILITIES BANNER (port of core_abilities_inc.php, PC/main branch — NOT the EV branch the
//    creature card renders: col-lg-4 columns, campaign-gated rest buttons, _main_ actions target) ─
function buildPcCoreAbilities(blob: PlayerBlob, casterId: string, sideInfo: boolean): string {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    const teamRaw = ec.team;
    const team = typeof teamRaw === 'object' && teamRaw !== null
        ? String(itemsCol(teamRaw, 'item_id') ?? '')
        : String(teamRaw ?? '');
    const ecId = bucketScalar(ec, 'encounter_creature_id');
    const encId = bucketScalar(ec, 'encounter_id');
    const enc = uuidOk(encId) ? window.FateTide?.encounterCache?.get(encId) : undefined;
    const inCombat = enc ? (Number(encounterField(enc, 'in_combat') ?? 0) === 1 ? '1' : '0') : '0';
    const aps = bucketScalar(ec, 'aps') || '0';
    const reaction = bucketScalar(ec, 'reaction') || '0';
    const mana = bucketScalar(ec, 'mana') || '0';

    const rangeBtn = (abilityId: string, ap: string, range: string, dist: string, icon: string, title: string): string => `
        <div class="p-1"><button class="btn btn-info text-dark use-ability-range"
            data-ability_id="${abilityId}" data-item_type="ability" data-ap_required="${ap}"
            data-range="${range}" data-aoe_type="981032B8-4F0B-43C9-8D98-67C693E4DB42"
            data-aoe_distance="${dist}" data-aoe_distance_height="" data-persistent_aoe="0"
            data-aoe_follows_target="0" data-roll_type="AD80D31D-898B-66CE-0894-1335D11B1529"
            data-effect_only_affects_same_team_type="0" data-effect_only_affects_opposing_team_type="0"
            data-aoe_skip_targeting="1" data-caster_team="${esc(team)}" data-caster_in_combat="${inCombat}"
            data-caster_aps="${esc(aps)}" data-caster_reaction="${esc(reaction)}" data-caster_mana="${esc(mana)}"
            title="${esc(title)}">${iconPh(icon.toLowerCase(), 25, 25, title)}</button></div>`;

    const simpleBtn = (abilityId: string, icon: string, btnClass = 'btn btn-info text-dark', extraClass = ''): string => `
        <div class="p-1"><button class="${btnClass}${extraClass}"
            ${pcCardAbilityAttrs(abilityId)} data-ability_id="${abilityId}"
        >${iconPh(icon)}</button></div>`;

    // render_simple_trigger_ability_button() twin (RESOLVE) — sparkle wrapper + auto-fire toggle badge.
    const resolveActive = triggerIsActive(blob, AB_RESOLVE);
    const resolveBtn = `
        <div class="p-1"><div class="ft-trigger-ability-wrapper${resolveActive ? ' ft-trigger-active' : ''}">
            <button class="btn btn-info text-dark"
                ${pcCardAttrs('triggered-primary')}
                data-ability_id="${AB_RESOLVE}"
                data-ft-trigger-use-snippet="handleAbilityBtnSingleClick('${AB_RESOLVE}');"
                title="Resolve">${iconPh('resolve')}</button>
            <span class="ft-trigger-badge ${resolveActive ? 'ft-trigger-badge-active' : 'ft-trigger-badge-inactive'}"
                title="${resolveActive ? 'Auto-trigger active – click to disable' : 'Auto-trigger inactive – click to enable'}"
                ${pcCardAttrs('toggle-trigger', { abilityId: AB_RESOLVE, casterId: uuidOk(ecId) ? ecId : casterId, encounterId: uuidOk(encId) ? encId : '', active: resolveActive ? '1' : '0' })}></span>
        </div></div>`;

    const bleedGlow = hasStatus(blob, ST_BLEEDING) || hasStatus(blob, ST_MODERATE_BLEEDING) || hasStatus(blob, ST_SEVERE_BLEEDING);

    // Rest buttons: only when a campaign context resolves (the GM moves time forward).
    const campId = campaignIdOf(blob);
    let restBlock = '';
    if (campId !== '') {
        const shortCharges = abilityChargesById(blob, 'full_ability', AB_SHORT_REST);
        const shortIcon = shortCharges === '2' ? 'short_rest_2' : (shortCharges === '1' ? 'short_rest_1' : 'short_rest_0');
        restBlock = `
            <div class="mb-2 d-flex flex-wrap align-items-start gap-2">
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1">
                    <div class="p-1"><button class="btn btn-info"
                        ${pcCardAbilityAttrs(AB_SHORT_REST)} data-ability_id="${AB_SHORT_REST}"
                    >${iconPh(shortIcon)}</button></div>
                    <div class="p-1"><button class="btn btn-info"
                        ${pcCardAbilityAttrs(AB_REST)} data-ability_id="${AB_REST}"
                    >${iconPh('rest')}</button></div>
                </div>
            </div>`;
    }

    // Clear-triggers: verified user + a real EC row (standalone PC has none — matches PHP).
    const userId = window.FateTide?.user?.id ?? playerWindow()._ft_user_id ?? '';
    const clearTriggersBtn = (userId && uuidOk(ecId)) ? `
        <div class="p-1"><button type="button" class="btn btn-outline-warning" data-ft-bar-action="clear_triggers"
            title="Clear trigger overlays for this creature — restores default auto-fire (retaliation, etc.)."
            data-ft-clear-triggers-ec="${esc(ecId)}"
            data-ft-clear-triggers-enc="${esc(uuidOk(encId) ? encId : '')}"
            data-ft-clear-triggers-camp="${esc(uuidOk(campId) ? campId : '')}"
            ${pcCardAttrs('clear-triggers')}>${iconPh('clear_triggers')}</button></div>` : '';

    const saTarget = `ft_special_actions_main_${sanId(casterId)}`;

    return `
    <div class="ft-core-abilities border rounded px-1${sideInfo ? ' ft-core-abilities--sideinfo-host' : ''}">
        <h5 class="ft-core-abilities-title d-flex align-items-center mt-1 mb-0 py-1 ps-3 fs-6 bg-light text-dark bg-gradient rounded-top">
            <span class="flex-shrink-0 fw-bold">Core Abilities</span>
            <span class="ft-ability-view-toolbar-host ft-core-abilities-toolbar ms-2 d-inline-flex align-items-center" style="min-width:0;"></span>
        </h5>
        <div class="row m-0">
            <div class="col-lg-4 col-md-12 mt-2">
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">
                    ${rangeBtn(AB_SPOT_HIDDEN, '2', 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA', '20', 'spot_hidden_creatures', 'Spot Hidden Creatures')}
                    ${rangeBtn(AB_SEARCH_HIDDEN_INV, '3', '36D59FF4-6778-96ED-8664-F5D63638C77B', '10', 'search_for_hidden_inventory', 'Search for Hidden Inventory')}
                    ${resolveBtn}
                    ${simpleBtn(AB_SNAP_OUTTA_IT, 'snap_outta_it')}
                    ${simpleBtn(AB_STOP_BLEEDING, 'stop_the_bleeding', 'btn btn-info text-dark', bleedGlow ? ' ft-stop-bleeding-active' : '')}
                </div>
                ${restBlock}
            </div>
            <div class="col-lg-4 col-md-12 mt-2">
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">
                    ${simpleBtn(AB_CUSTOM_FULL, 'custom_full_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM, 'custom_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_QUICK, 'custom_quick_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_FREE, 'custom_free_ability', 'btn btn-muted')}
                    ${simpleBtn(AB_CUSTOM_REACTION, 'custom_reaction_ability', 'btn btn-muted')}
                </div>
                <div class="ft-core-ability-group d-flex flex-wrap align-items-center gap-1 mb-2">
                    <div class="p-1"><button class="btn btn-danger-soft" data-ft-bar-action="add_ap" ${pcCardAttrs('add-ap')}>${iconPh('add_ap')}</button></div>
                    <div class="p-1"><button class="btn btn-danger-soft" data-ft-bar-action="add_reaction" ${pcCardAttrs('add-reaction')}>${iconPh('add_reaction')}</button></div>
                    ${clearTriggersBtn}
                </div>
            </div>
            <div id="${saTarget}" class="col-lg-4 col-md-12 mt-2"></div>
        </div>
    </div>`;
}

// ── ABILITIES TAB (port of _abilities_inc.php, PC path: class-mechanic bars LIVE) ────────────────
function buildPcAbilitiesTab(blob: PlayerBlob, casterId: string, sideInfo: boolean, canEdit: boolean): string {
    const rootId = `ft_sheet_abilities_${casterId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const searchId = `${rootId}_search`;
    const banner = canEdit
        ? paneMount('pc_core_abilities', casterId, `data-ft-side-info="${sideInfo ? '1' : '0'}"`)
        : '<div class="ft-ability-view-toolbar-bar d-flex align-items-center mb-2"><span class="ft-ability-view-toolbar-host"></span></div>';

    // The class-mechanic charge bars live in the abilities-tab SHELL, NOT a .ft-pc-sheet-pane, so
    // ftRerenderPcSheet never repaints them — a charge spend elsewhere (weapons-tab strip, ability
    // use, rest) left this slider stale until reopen. Wrap in a keyed host so ftRerenderClassMechanicBars
    // (called from applyCasterSideEffects on caster_update) can repaint it in place. [[live charge sync]]
    return `<div data-ft-class-mechanic-host="${esc(casterId)}" data-ft-sideinfo="${sideInfo ? '1' : '0'}" data-ft-canedit="${canEdit ? '1' : '0'}">${buildClassMechanicBars(blob, sideInfo, canEdit)}</div>${ABILITIES_TAB_STYLE}
    <div class="fate-tide-sheet-abilities-search-root${sideInfo ? ' ft-abilities--sideinfo-host' : ''}" id="${rootId}">
        <div class="ft-abilities-header-wrap">
            ${banner}
            <div class="row g-2 align-items-center my-3 w-100 mx-0 ft-abilities-search-row">
                <div class="col-12 col-md-auto ms-md-auto ft-abilities-search-anchor">
                    <label class="visually-hidden" for="${searchId}">Search abilities</label>
                    <div class="position-relative">
                        <input type="search" id="${searchId}" class="form-control form-control-sm fate-tide-sheet-ability-search pe-4"
                            data-ft-sheet-abilities-root="${rootId}" placeholder="Search abilities…" autocomplete="off">
                        <svg class="svg-icon position-absolute top-50 end-0 translate-middle-y me-2 ft-sheet-search-icon" aria-hidden="true"><use href="#search"></use></svg>
                    </div>
                </div>
            </div>
        </div>
        ${paneMount('ability_sections', casterId, ' data-ft-static-creature="0"')}
    </div>`;
}

// ── SUMMONS TAB (port of _summons_inc.php pill strip; sub-SHEETS stay lazy via
//    ftEnsureSummonSheetLoaded — that part is blessed) ──────────────────────────────────────────
// Blob-first since 2026-06-12: the EC bucket bakes caster_summons = [{encounter_creature_id,
// encounter_creature_name}] right next to count_caster_summons (fetch_encounter_creatures.php),
// so count and list can never disagree and the strip renders with zero AJAX. Summon ROWS stay
// un-nested from encounter_creatures_arr (summons-plan design constraint) — only this thin list
// rides the blob.
/** Returns the strip html, '' when the PC has no summons, or null when the blob predates the
 *  caster_summons bake (count > 0 but no list key) → caller keeps the lazy-AJAX fallback. */
export function buildSummonsTab(blob: PlayerBlob, sideInfo: boolean): string | null {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    const count = Number(bucketScalar(ec, 'count_caster_summons') || 0);
    if (!(count > 0)) return '';
    const rawList = ec.caster_summons;
    if (!Array.isArray(rawList)) return null; // stale pre-bake blob → summons_tab_ajax_inc.php

    const summons: Array<{ id: string; name: string }> = [];
    for (const row of rawList) {
        if (!row || typeof row !== 'object') continue;
        const sid = bucketScalar(row, 'encounter_creature_id');
        if (!uuidOk(sid)) continue;
        summons.push({ id: sid, name: bucketScalar(row, 'encounter_creature_name') });
    }

    // Which summon pill is "active" comes from the live JS cache (the caster you're acting as), not a
    // cookie (de-cookie 2026-06-24): after setCasterSummon the active caster IS the summon, and on a
    // same-tab reload the sidelog seed restores it from sessionStorage. Case-insensitive id compare.
    const w = playerWindow();
    const selected = ((typeof w.ftGetActiveCasterId === 'function'
        ? String(w.ftGetActiveCasterId() || '')
        : '')).toUpperCase();
    const contentCol = sideInfo ? 'col-12' : 'col-lg-7 col-md-12';

    let pills = '';
    let panes = '';
    for (const s of summons) {
        const active = selected !== '' && selected === String(s.id).toUpperCase();
        pills += `<li class="nav-item p-1" role="presentation">
            <button class="nav-link border rounded btn_caster_summons_tab ${active ? 'active' : ''}"
                id="tab_${esc(s.id)}" data-bs-target="#div_${esc(s.id)}" aria-controls="div_${esc(s.id)}"
                data-bs-toggle="pill" type="button" role="tab" ${active ? "aria-selected='true'" : ''}
                ${pcCardAttrs('set-summon', { summonId: s.id })}>${esc(s.name)}</button>
        </li>`;
        panes += `<div class="tab-content pl-1 pr-1 ${contentCol}" style="scrollbar-width: thin;">
            <div class="tab-pane fade ${active ? 'show active' : ''}" id="div_${esc(s.id)}" aria-labelledby="tab_${esc(s.id)}" role="tabpanel">
                <div id="div_caster_sheet_ec_${hexOf(s.id)}" data-ft-summon-ec="${esc(s.id)}">
                    <div class="d-flex justify-content-center p-3"><div class="spinner-border" role="status"></div></div>
                </div>
            </div>
        </div>`;
    }
    return `<div>
        <div><ul class="nav nav-pills pt-1 pb-1" id="nav_tabs_caster_summons_console" role="tablist">${pills}</ul></div>
        ${panes}
    </div>`;
}

// ── ENCOUNTER TAB (port of _encounter_inc.php; explorable links stay a lazy server fetch) ───────
// Blob-first since 2026-06-12: name / location-parent / picture carousel / description render from
// the encounter blob (encounterCache). ONLY the explorable-links block lazy-fetches
// (links_only=1, first shown.bs.tab) — build_explorable_encounter_links is a campaign-graph walk
// whose output is viewer-permission-dependent (private links), so it can never bake into the
// shared blob. Picture carousel reached parity 2026-06-13 (buildCarousel, _encounter_inc.php twin —
// encounter pics have no set-pic buttons; only blob-preloaded media renders).
/** Returns the tab html, or null when the encounter blob / campaign context isn't resolvable
 *  client-side → caller keeps the legacy whole-tab lazy-AJAX fallback. */
export function buildEncounterTab(blob: PlayerBlob, casterId: string, suffix: string, sideInfo: boolean): string | null {
    const encId = bucketScalar(recordOf(blob.encounter_creatures) ?? {}, 'encounter_id');
    if (!uuidOk(encId)) return null;
    const enc = recordOf(window.FateTide?.encounterCache?.get(encId));
    if (!enc) return null;
    const encName = String(encounterField(enc, 'encounter_name') ?? '');
    if (encName === '') return null;          // scrubbed/stub blob → the server include's own gates
    if (campaignIdOf(blob) === '') return null; // PHP shows "No Encounter Loaded" sans campaign

    const col = sideInfo ? 'col-12' : 'col-lg-6 col-md-12';
    const locName = String(encounterField(enc, 'encounter_location_parent_name') ?? '');

    const carouselHtml = buildCarousel({
        domId: `enc_${hexOf(encId)}${suffix}`,
        itemName: encName,
        pics: pictureListOf(enc, ['encounter_picture']),
        videos: itemVideos(enc),
    });
    const picHtml = carouselHtml
        ? `<div class="mb-2 d-flex align-items-center justify-content-center">${carouselHtml}</div>`
        : '';
    const desc = encounterField(enc, 'encounter_description');
    const descHtml = (desc !== null && desc !== undefined && String(desc) !== '')
        ? `<div class="cl_encounter_description p-2 mb-2 border border-2 rounded bg-gradient text-left">${ptMount(desc)}</div>`
        : '';

    return `<div class="ft-encounter-sheet${sideInfo ? ' ft-encounter-sheet--sideinfo-host' : ''}">
        <div class="text-center">
            <h3 class="mt-1 mb-2">${esc(encName)}</h3>
            ${locName !== '' ? `<h4 class="mt-0">${esc(locName)}</h4>` : ''}
        </div>
        <div class="row m-0">
            <div class="${col} p-2">${picHtml}${descHtml}</div>
            <div class="${col} p-2">
                <div class="ft-encounter-minimap-mount mb-2" data-campaign-id="${esc(campaignIdOf(blob))}" data-encounter-id="${esc(encId)}" data-encounter-name="${esc(encName)}" data-perspective="player"></div>
                <div id="ft_pc_lazy_enc_links_${sanId(casterId)}${suffix}" data-ft-lazy-encounter-links="${esc(encId)}"></div>
            </div>
        </div>
    </div>`;
}

// ── CONSOLE TABS (port of the shell's pill nav + pane wrappers) ──────────────────────────────────
const PC_CONSOLE_TABS = [
    'skills', 'abilities', 'weapons_equipped', 'movement', 'passive_abilities', 'castable_spells',
    'prepare_spells', 'mentas', 'inventory', 'crafting', 'character_description', 'player_notes',
    'defenses', 'summons', 'encounter', 'player_settings',
] as const;
type PcConsoleTab = typeof PC_CONSOLE_TABS[number];

function isPcConsoleTab(value: string): value is PcConsoleTab {
    return (PC_CONSOLE_TABS as readonly string[]).includes(value);
}

// Creature-sheet cookie values map back to their PC tab so the active tab survives switching
// between a creature card and a PC card (inverse of the creature card's PC→creature map).
const CREATURE_TAB_TO_PC: Record<string, string> = {
    creature_skills: 'skills',
    creature_abilities: 'abilities',
    creature_weapons_equipped: 'weapons_equipped',
    creature_movement: 'movement',
    creature_passive_abilities: 'passive_abilities',
    creature_castable_spells: 'castable_spells',
    creature_mentas: 'mentas',
    creature_inventory: 'inventory',
    creature_description: 'character_description',
    creature_defenses: 'defenses',
};

function consoleTabPill(key: PcConsoleTab, suffix: string, casterId: string, active = false): string {
    return `<li class="nav-item p-1" role="presentation">
        <button id="tab_${key}${suffix}" class="nav-link border ${active ? 'active' : ''}"
            data-bs-target="#div_${key}${suffix}" aria-controls="div_${key}${suffix}"
            data-bs-toggle="pill" type="button" role="tab" ${active ? "aria-selected='true'" : ''}
            ${pcCardAttrs('tab-click', { tabKey: key, casterId })}
        >${iconPh(key)}</button></li>`;
}

function buildPcConsoleTabs(blob: PlayerBlob, casterId: string, suffix: string, sideInfo: boolean): string {
    const ec = recordOf(blob.encounter_creatures) ?? {};
    const canEdit = hasPlayerPermission(blob);

    // Pill visibility (port of $ft_pc_tab_is_visible).
    const casterTypeIds = layerEntriesOf(blob, 'bending_caster_type')
        .map((e) => String(layerListEntryUuid(e) ?? ''))
        .filter((id) => uuidOk(id));
    const isFullCaster = casterTypeIds.includes(CASTER_TYPE_FULL);
    const isHalfCaster = casterTypeIds.includes(CASTER_TYPE_HALF);
    const hasCasterType = casterTypeIds.length > 0;
    const hasInnateOrAtwill = layerHas(blob, 'innate_spell_per_long_rest') || layerHas(blob, 'at_will_spell');
    // Mentas relocated to computed.creature.abilities.menta — read that bucket directly (module layer
    // readers have no abilities bridge and mentas aren't carrier-exposed, so layerOf/layerHas miss them).
    // [[project_caster_arr_source_table_rebuild]]
    const hasMenta = hasMentaBucket(blob);
    const skills = recordOf(computedCreature(blob)?.skills);
    const hasCrafting = recordOf(skills?._meta)?.has_crafting === true;
    const countSummons = Number(bucketScalar(ec, 'count_caster_summons') || 0);

    const tabVisible = (tab: string): boolean => {
        if (tab === 'castable_spells' && hasCasterType && !isFullCaster && !isHalfCaster && !hasInnateOrAtwill) return false;
        if (tab === 'prepare_spells' && !canEdit) return false;
        if (tab === 'prepare_spells' && hasCasterType && !isFullCaster && !isHalfCaster) return false;
        if (tab === 'player_notes' && !canEdit) return false;
        if (tab === 'mentas' && !hasMenta) return false;
        if (tab === 'crafting' && !hasCrafting) return false;
        if (tab === 'summons' && countSummons === 0) return false;
        return true;
    };
    const visible = PC_CONSOLE_TABS.filter(tabVisible);
    // One or two visible tabs form a single row; only larger consoles need the compact two-row split.
    const colsPerRow = visible.length <= 2
        ? Math.max(1, visible.length)
        : Math.max(1, Math.ceil(visible.length / 2));

    // Active tab from the shared sheet cookie; creature_* values map back to their PC tab. The PHP
    // leaves NOTHING active when the cookie names a hidden tab — normalize to 'abilities' instead.
    let active: PcConsoleTab = 'abilities';
    const rawCookie = cookieVal('cookie_player_character_sheet_menu_tab');
    if (rawCookie) {
        const sfx = rawCookie.replace(/^tab_/, '');
        const requested = CREATURE_TAB_TO_PC[sfx] ?? sfx;
        if (isPcConsoleTab(requested)) active = requested;
    }
    if (!visible.includes(active)) active = 'abilities';

    let pills = '';
    for (const key of visible) {
        const isActive = key === active;
        // VIEW-IS-READ-ONLY (caster-persistence fix 2026-06-22): a tab click re-asserts only your OWN
        // home creature (ftAssertSelfCasterOnTab); viewing another player's PC can't hijack #caster_id.
        pills += consoleTabPill(key, suffix, casterId, isActive);
    }

    const pane = (key: string, inner: string, extraClass = ''): string => `
        <div id="div_${key}${suffix}" class="tab-pane fade${extraClass} ${key === active ? 'show active' : ''}"
            aria-labelledby="tab_${key}${suffix}" role="tabpanel">${inner}</div>`;

    // Encounter tab: blob-first (name/picture/description from encounterCache; ONLY the
    // explorable-links block lazy-fetches — see ENCOUNTER TAB above). null = encounter blob not
    // cached client-side → legacy whole-tab lazy-AJAX fallback.
    const encId = bucketScalar(ec, 'encounter_id');
    const encounterBody = buildEncounterTab(blob, casterId, suffix, sideInfo);
    const lazyEncounterId = `ft_pc_lazy_encounter_${sanId(casterId)}${suffix}`;
    const encounterInner = encounterBody !== null
        ? encounterBody
        : (uuidOk(encId)
            ? `<div id="${lazyEncounterId}" data-ft-lazy-encounter="${esc(encId)}">
                <div class="d-flex justify-content-center p-3"><div class="spinner-border" role="status"></div></div>
            </div>`
            : '<div class="p-3"><h5>No Encounter Loaded</h5></div>');

    // Summons tab: blob-first strip from the baked caster_summons list (see SUMMONS TAB above).
    // null = stale pre-bake blob (count > 0, no list key) → lazy-AJAX fallback via the legacy
    // endpoint, kept ONLY for that staleness window.
    const summonsStrip = buildSummonsTab(blob, sideInfo);
    const summonsEcId = bucketScalar(ec, 'encounter_creature_id');
    // The host div is the WS repaint anchor (ftRerenderSummonsStrip): always emitted, even with no
    // summons, so a live roster change can find where the strip belongs without a full card render.
    const summonsInner = `<div data-ft-summons-host="${esc(casterId)}" data-ft-summons-sideinfo="${sideInfo ? '1' : '0'}">${summonsStrip !== null
        ? summonsStrip
        : ((countSummons > 0 && uuidOk(summonsEcId))
            ? `<div id="ft_pc_lazy_summons_${sanId(casterId)}${suffix}" data-ft-lazy-summons="${esc(summonsEcId)}">
                <div class="d-flex justify-content-center p-3"><div class="spinner-border" role="status"></div></div>
            </div>`
            : '')}</div>`;

    // Pane emission mirrors the PHP shell: most panes unconditional, prepare/notes permission-gated,
    // mentas layer-gated (pill visibility is a separate, narrower filter — exactly like the PHP).
    let panes = '';
    panes += pane('skills', paneMount('skills', casterId));
    panes += pane('abilities', buildPcAbilitiesTab(blob, casterId, sideInfo, canEdit));
    panes += pane('weapons_equipped', buildWeaponsTab(blob, casterId, sideInfo));
    panes += pane('movement', paneMount('movement', casterId));
    panes += pane('passive_abilities', buildPassiveAbilitiesSearchWrap(casterId, paneMount('passive_abilities', casterId)));
    panes += pane('castable_spells', paneMount('castable_spells', casterId));
    if (canEdit) panes += pane('prepare_spells', paneMount('prepare_spells', casterId));
    if (hasMenta) panes += pane('mentas', paneMount('mentas', casterId));
    panes += pane('inventory', paneMount('inventory', casterId));
    panes += pane('equipment_slots', paneMount('equipment_slots', casterId));
    panes += pane('crafting', paneMount('crafting', casterId));
    panes += pane('character_description', paneMount('character_description', casterId), ' text-left');
    if (canEdit) panes += pane('player_notes', paneMount('player_notes', casterId), ' text-left');
    panes += pane('defenses', paneMount('defenses', casterId), ' text-left');
    panes += pane('summons', summonsInner, ' text-left');
    panes += pane('encounter', encounterInner, ' text-left');
    panes += pane('player_settings', paneMount('player_settings', casterId), ' text-left');

    return `
    <div${sideInfo ? ' class="mt-2"' : ''}>
        <ul class="nav nav-pills pt-1 pb-1 border border-bottom-0 rounded-top character-console-nav${sideInfo ? ' character-console-nav--sideinfo' : ''}"
            id="nav_tabs_player_character_console${suffix}" role="tablist"
            ${sideInfo ? `style="--ft-console-cols: ${colsPerRow};"` : ''}>${pills}</ul>
        <div class="tab-content border overflow-auto pl-1 pr-1" style="height: 595px; scrollbar-width: thin;">${panes}</div>
    </div>`;
}

// ── MAIN ENTRY ───────────────────────────────────────────────────────────────────────────────────
export interface PlayerCardOpts extends EntityCardPaintHooks {
    sideInfo?: boolean;
    cardColClasses?: string; // accepted for dispatcher-signature compat; the PC shell has no card col
    viewTarget?: string;
    /** Unique full-sheet window suffix. Empty preserves the canonical controlled-actor DOM ids. */
    domSuffix?: string;
    // Explicit read-only inspection from an encounter surface. This never grants control: it only
    // permits a PC card already present in this viewer's redacted encounter projection to render.
    readOnlyInspect?: boolean;
}

/** Gate trace: every server-fallback exit logs WHY (visible via ftDebugLog 'caster' channel) so a
 *  blank/fallback smoke is diagnosable from the console instead of silent. */
function pcCardGate(reason: string, detail?: unknown): false {
    try { ftDebugLog('caster', { playerCardGate: reason, detail }); } catch (e) { /* logger absent */ }
    return false;
}

function ftRenderPlayerCardFromCache(
    casterId: string,
    container: HTMLElement | string,
    opts: PlayerCardOpts = {},
): boolean {
    try {
        const target = typeof container === 'string' ? document.getElementById(container) : container;
        if (!casterId || !target) return pcCardGate('no-target', casterId);
        const w = playerWindow();
        if (typeof w.ftMountPcSheet !== 'function') return pcCardGate('no-ftMountPcSheet');

        let blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        if (!blob && typeof w.ftSeedCasterFromEncounterCache === 'function') {
            w.ftSeedCasterFromEncounterCache(casterId);
            blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        }
        if (!blob) return pcCardGate('cache-miss', casterId);
        if (holdPendingEntityCard(blob, target, compiled => {
            if (window.FateTide?.casterCache?.get(casterId) === blob) window.FateTide.casterCache.set(casterId, compiled);
            return ftRenderPlayerCardFromCache(casterId, target, opts);
        }, { onPaintUnavailable: opts.onPaintUnavailable })) return true;
        if (typeof blob._entity_refusal === 'string') {
            replaceHydratedHtml(target, `<div class="alert alert-warning" role="alert">${esc(blob._entity_refusal)}</div>`);
            opts.afterPaint?.();
            return true;
        }

        // The PC caster ROOT has no items bucket/item_type — the type lives on encounter_creatures.
        const itemType = String(
            encounterCreatureField(blob.encounter_creatures, 'item_type')
            ?? (recordOf(blob.player_characters) ? 'player_character' : itemsCol(blob, 'item_type'))
            ?? ''
        ).toLowerCase();
        if (itemType !== 'player_character') return pcCardGate('not-pc', itemType);

        const ecId = bucketScalar(blob.encounter_creatures, 'encounter_creature_id');
        const id = uuidOk(ecId)
            ? ecId
            : String(itemsCol(blob, 'item_id') ?? bucketScalar(blob.player_characters, 'item_id') ?? '');
        if (!uuidOk(id)) return pcCardGate('no-id', { casterId, ecId });

        const explicitTarget = String(opts.viewTarget ?? '');
        const explicitTargetMatches = uuidOk(explicitTarget)
            && explicitTarget.toUpperCase() === id.toUpperCase();
        // A separate FT3D sheet window is presentation-only even for a GM/controller whose direct
        // permission would otherwise make this the editable primary sheet. Encounter projection is
        // still the only authorization carve-out for a viewer with NO direct permission; a direct
        // player/campaign grant is already an independently-authorized way to view this caster.
        const isReadOnlyInspect = opts.readOnlyInspect === true
            && explicitTargetMatches
            ;
        if (!hasPlayerPermission(blob) && !hasCampaignPermission(blob) && !isReadOnlyInspect) {
            return pcCardGate('no-permission');
        }
        // (No dead/dying gate since 2026-06-12: the PHP PC shell has NO dead branch — the sheet
        // renders identically and the inventory pane derives its loot mode from the blob itself.
        // Explicit inspection paints the already-authorized cache read-only.)
        // (No natural_weapon gate: renderNaturalWeapons reads the RAW layer + the computed cluster
        // itself — the PHP shell only suppresses the TS pane to avoid duplicating its own legacy
        // table, which doesn't exist on this path.)

        // The pane mounts key on the CANONICAL id (blob's own ec id / item id) — make sure the
        // cache resolves under that exact key too (the open call may have used a differently-cased
        // or alias key), or ftMountPcSheet's getCaster() misses and every pane silently stays blank.
        try {
            const cache = window.FateTide?.casterCache;
            if (cache && !cache.get(id)) cache.set(id, blob);
        } catch (e) { /* non-fatal */ }

        const sideInfo = !!opts.sideInfo;
        const windowSuffix = String(opts.domSuffix ?? '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 64);
        const suffix = sideInfo ? `_ec_${hexOf(id)}` : windowSuffix ? `_win_${windowSuffix}` : '';
        const canEdit = hasPlayerPermission(blob);

        const lastUpdated = Array.isArray(blob.item_arr_last_t_updated)
            ? blob.item_arr_last_t_updated[0]
            : blob.item_arr_last_t_updated;

        const html = `
        <div id='div_refresh_player_character_sheet${suffix}'>
        <div class="mh-100">
            <div id="div_caster_sheet${suffix}" data-ft-pc-owns-sheet="1" data-ft-caster-id="${esc(id)}" data-ft-player-card-cache="1" data-ft-read-only-inspect="${isReadOnlyInspect ? '1' : '0'}">
                <input type="hidden" class="ft-input-active-encounter-caster" id="input_active_caster_sheet${suffix}" value="${esc(id)}">
                <input type="hidden" id="input_last_updated${suffix}" value="${esc(lastUpdated ?? '')}">
                ${sideInfo && uuidOk(ecId) ? `<input id="input_hidden_sidelog_encounter_creature" type="hidden" value="${esc(ecId)}">` : ''}
                ${paneMount('summary', id, ` data-ft-pc-side-info="${sideInfo ? '1' : '0'}" data-ft-pc-dom-suffix="${esc(suffix)}"`)}
                ${!sideInfo && !isReadOnlyInspect ? `<div class="ft-pc-sheet-pane ft-pc-actionbar-dock" data-ft-pc-pane="action_bar" data-ft-caster-id="${esc(id)}"></div>` : ''}
                ${buildPcConsoleTabs(blob, id, suffix, sideInfo)}
            </div>
        </div>
        </div>`;

        replaceHydratedHtml(target, html);

        // ── hydration (the PHP path runs these via inline bootstraps / loadPostFunctions) ─────────
        try { w.ftMountPcSheet(target); } catch (e) { try { w.ftMountPcSheet(); } catch (e2) { /* re-scan covers */ } }

        // SELF-HEAL: if not a single pane actually painted (renderer threw per-pane, cache key
        // unresolvable, …), a committed blank card is worse than the PHP shell — log loudly and
        // return false so template.js's fallback queryData overwrites this innerHTML.
        if (!target.querySelector('.ft-pc-sheet-pane[data-ft-pc-done="1"]')) {
            console.warn('[pc-card] zero panes mounted — falling back to the server sheet', { casterId, id });
            return pcCardGate('panes-failed-to-mount', { casterId, id });
        }
        if (canEdit) {
            try {
                const sa = document.getElementById(`ft_special_actions_main_${sanId(id)}`);
                if (sa) w.renderCasterSpecialActionsInline?.(id, sa.id, false);
            } catch (e) { /* non-fatal */ }
        }
        try { w.ftMountAbilityViewToolbars?.(); } catch (e) { /* non-fatal */ }
        try { w.ftMountIcons?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountRuneIcons?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountThumbnails?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParsedText?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountParseTextTables?.(target); } catch (e) { /* footer pass covers */ }
        try { w.ftMountInventoryTiles?.(target); } catch (e) { /* footer pass covers */ }
        try { w.refreshDragAndDrop?.(); } catch (e) { /* non-fatal */ }

        // Lazy tabs (summons + encounter): fetch the server-bound content once, on first
        // shown.bs.tab (or immediately when the tab IS the active one). queryData's
        // loadPostFunctions runs the standard post-inject hydration the includes' output needs
        // (incl. priming the active summon pill's sub-sheet).
        const wireLazyTab = (tabBtnId: string, lazyAttr: string, url: string, data: Record<string, string>): void => {
            try {
                const lazyHost = target.querySelector(`[${lazyAttr}]`) as HTMLElement | null;
                const tabBtn = document.getElementById(tabBtnId);
                if (!lazyHost || !tabBtn) return;
                const load = (): void => {
                    if (lazyHost.getAttribute('data-ft-loaded') === '1') return;
                    lazyHost.setAttribute('data-ft-loaded', '1');
                    w.queryData?.(url, lazyHost.id, data);
                };
                tabBtn.addEventListener('shown.bs.tab', load);
                if (tabBtn.classList.contains('active')) load();
            } catch (e) { /* non-fatal */ }
        };
        wireLazyTab(
            `tab_encounter${suffix}`,
            'data-ft-lazy-encounter',
            `${BASE_URL}options/game/character_sheet_display/encounter/encounter_tab_ajax_inc.php`,
            {
                encounter_id: (target.querySelector('[data-ft-lazy-encounter]') as HTMLElement | null)?.getAttribute('data-ft-lazy-encounter') ?? '',
                opened_in_side_info: sideInfo ? '1' : '',
            },
        );
        // Blob-first encounter tab: only the explorable-links block needs the server (the host
        // div exists only when buildEncounterTab painted; the whole-tab wiring above no-ops then).
        wireLazyTab(
            `tab_encounter${suffix}`,
            'data-ft-lazy-encounter-links',
            `${BASE_URL}options/game/character_sheet_display/encounter/encounter_tab_ajax_inc.php`,
            {
                encounter_id: (target.querySelector('[data-ft-lazy-encounter-links]') as HTMLElement | null)?.getAttribute('data-ft-lazy-encounter-links') ?? '',
                links_only: '1',
            },
        );
        // Hydrate the blob-painted encounter tab's mini-map mount (.ft-encounter-minimap-mount,
        // buildEncounterTab) — the encounter-minimap bundle's page-load scan predates this inject
        // and template.js's loadPostFunctions re-invoke never runs on the blob-first path.
        // Scoped to THIS card's container so a document-wide rescan can't prematurely hydrate
        // unrelated lazy mounts (e.g. the XZ fullscreen slot). Idempotent
        // (data-ft-minimap-mounted); no-op when the legacy whole-tab fallback painted instead
        // (the server include ships its own mount and loadPostFunctions rescans then).
        if (typeof w.ftMountEncounterMinimaps === 'function') {
            try { w.ftMountEncounterMinimaps(target); } catch (e) { /* non-fatal */ }
        }
        wireLazyTab(
            `tab_summons${suffix}`,
            'data-ft-lazy-summons',
            `${BASE_URL}options/game/character_sheet_display/summons/summons_tab_ajax_inc.php`,
            {
                encounter_creature_id: (target.querySelector('[data-ft-lazy-summons]') as HTMLElement | null)?.getAttribute('data-ft-lazy-summons') ?? '',
                opened_in_side_info: sideInfo ? '1' : '',
            },
        );

        // Blob-rendered summons strip: when the summons tab is ALREADY active with a
        // cookie-preselected pill, no shown.bs.tab fires — prime that pill's sub-sheet here (the
        // same guarded prime loadPostFunctions runs after AJAX injects; idempotent via
        // data-ft-summon-loaded, offsetParent gates on actual visibility).
        try {
            const activePill = target.querySelector('#nav_tabs_caster_summons_console .btn_caster_summons_tab.active') as HTMLElement | null;
            if (activePill && activePill.offsetParent !== null && typeof w.ftEnsureSummonSheetLoaded === 'function') {
                void w.ftEnsureSummonSheetLoaded(activePill.id.replace(/^tab_/, ''));
            }
        } catch (e) { /* non-fatal */ }

        if (sideInfo) {
            try { setOpenSideInfo(id, target.id); } catch (e) { /* non-fatal */ }
        }

        ftDebugLog('caster', { playerCardCacheRender: id, sideInfo });
        opts.afterPaint?.();
        return true;
    } catch (e) {
        console.warn('ftRenderPlayerCardFromCache failed', e);
        return false;
    }
}

// ── WS SUMMONS-STRIP REPAINT ─────────────────────────────────────────────────────────────────────
// Applied births and caster updates share this small projection. The strip and
// its navigation pill update in place, preserving already loaded summon sheets.

/** Repaint the summons pill strip for `casterId` (the CONTROLLER) from the cached blob.
 *  Cheap no-op when no strip host is on screen for this caster or the roster is unchanged.
 *  Already-loaded summon sub-sheets survive the swap (detached + re-adopted). */
function ftRerenderSummonsStrip(casterId: string): void {
    try {
        if (!casterId) return;
        const want = String(casterId).toUpperCase();
        const hosts = Array.from(document.querySelectorAll<HTMLElement>('[data-ft-summons-host]'));
        const host = hosts.find((el) => (el.getAttribute('data-ft-summons-host') || '').toUpperCase() === want);
        if (!host) return;

        const blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        if (!blob) return;
        const ec = recordOf(blob.encounter_creatures) ?? {};
        const count = Number(bucketScalar(ec, 'count_caster_summons') || 0);
        const rawList = ec.caster_summons;
        if (count > 0 && !Array.isArray(rawList)) return; // stale pre-bake blob → leave the lazy-AJAX content alone

        // New roster from the blob; old roster from the pills currently in the DOM.
        const next: Array<{ id: string; name: string }> = [];
        if (count > 0 && Array.isArray(rawList)) {
            for (const row of rawList) {
                if (!row || typeof row !== 'object') continue;
                const sid = bucketScalar(row, 'encounter_creature_id');
                if (!uuidOk(sid)) continue;
                next.push({ id: String(sid).toUpperCase(), name: bucketScalar(row, 'encounter_creature_name') });
            }
        }
        const prev: Array<{ id: string; name: string }> = Array.from(
            host.querySelectorAll<HTMLElement>('.btn_caster_summons_tab'),
        ).map((p) => ({ id: p.id.replace(/^tab_/, '').toUpperCase(), name: (p.textContent || '').trim() }));
        const rosterKey = (l: Array<{ id: string; name: string }>): string =>
            l.map((s) => `${s.id}\u0001${s.name}`).join('\u0002');
        if (rosterKey(prev) === rosterKey(next)) return; // unchanged — every controller caster_update lands here

        // If you were ACTING AS a summon that just left the roster, revert control to the
        // controller (this card's caster) — otherwise the session keeps acting as a dead row
        // (the old dangling-selection knock-on from the summons plan's closure note).
        const w = playerWindow();
        const active = String(typeof w.ftGetActiveCasterId === 'function' ? (w.ftGetActiveCasterId() || '') : '').toUpperCase();
        const nextIds = new Set(next.map((s) => s.id));
        if (active !== '' && !nextIds.has(active) && prev.some((s) => s.id === active)) {
            try {
                w.ftSetSelectedSummon?.('');
                void w.ftSetActiveCaster?.(casterId);
            } catch (e) { /* non-fatal */ }
        }

        // The pane is always mounted. Change its navigation pill alongside the strip.
        if ((prev.length === 0) !== (next.length === 0)) {
            const pane = host.closest<HTMLElement>('.tab-pane');
            const suffix = pane?.id.startsWith('div_summons') ? pane.id.slice('div_summons'.length) : null;
            const nav = suffix === null ? null : document.getElementById('nav_tabs_player_character_console' + suffix);
            if (nav && suffix !== null) {
                const pill = document.getElementById('tab_summons' + suffix);
                if (next.length && !pill) nav.insertAdjacentHTML('beforeend', consoleTabPill('summons', suffix, casterId));
                else if (!next.length && pill) {
                    if (pill.classList.contains('active')) document.getElementById('tab_abilities' + suffix)?.click();
                    pill.closest('li')?.remove();
                }
                const count = nav.children.length;
                nav.style.setProperty('--ft-console-cols', String(count <= 2 ? Math.max(1, count) : Math.ceil(count / 2)));
            }
        }

        // Surgical swap: rebuild the strip, keeping already-loaded summon sub-sheets alive —
        // grabbing the node references first keeps them valid after innerHTML discards them.
        const sideInfo = host.getAttribute('data-ft-summons-sideinfo') === '1';
        const preserved = new Map<string, HTMLElement>();
        host.querySelectorAll<HTMLElement>('[data-ft-summon-ec]').forEach((el) => {
            if (el.dataset.ftSummonLoaded === '1') {
                preserved.set((el.getAttribute('data-ft-summon-ec') || '').toUpperCase(), el);
            }
        });
        host.innerHTML = buildSummonsTab(blob, sideInfo) ?? '';
        host.querySelectorAll<HTMLElement>('[data-ft-summon-ec]').forEach((el) => {
            const kept = preserved.get((el.getAttribute('data-ft-summon-ec') || '').toUpperCase());
            if (kept) el.replaceWith(kept);
        });

        // Prime the active pill's sub-sheet when the strip is actually visible (same guarded prime
        // the card render runs; idempotent via data-ft-summon-loaded).
        const activePill = host.querySelector('.btn_caster_summons_tab.active') as HTMLElement | null;
        if (activePill && activePill.offsetParent !== null && typeof w.ftEnsureSummonSheetLoaded === 'function') {
            void w.ftEnsureSummonSheetLoaded(activePill.id.replace(/^tab_/, ''));
        }
        ftDebugLog('caster', { summonsStripRerender: casterId, prev: prev.length, next: next.length });
    } catch (e) {
        console.warn('ftRerenderSummonsStrip failed', e);
    }
}

/** Repaint the abilities-tab class-mechanic charge bars for `casterId` from the cached blob. Those
 *  bars (the icon + slider + current/max readout at the top of the abilities tab) sit in the tab
 *  SHELL, NOT a .ft-pc-sheet-pane, so ftRerenderPcSheet's surgical pass never touches them — a
 *  charge change made anywhere else (the weapons-tab ± strip, an ability that spends charges, a
 *  rest) left this slider stale until the card was reopened, even though the weapons-tab strip
 *  (a real pane) updated live. Repaint the host in place from the fresh blob. Cheap no-op when no
 *  host for this caster is on screen; skips a host whose slider is mid-drag so we don't yank it. */
function ftRerenderClassMechanicBars(casterId: string): void {
    try {
        if (!casterId) return;
        const want = String(casterId).toUpperCase();
        const hosts = Array.from(document.querySelectorAll<HTMLElement>('[data-ft-class-mechanic-host]'));
        if (hosts.length === 0) return;
        const blob = recordOf(window.FateTide?.casterCache?.get(casterId));
        if (!blob) return;
        for (const host of hosts) {
            if ((host.getAttribute('data-ft-class-mechanic-host') || '').toUpperCase() !== want) continue;
            // Don't yank a slider the user is holding (:active pointer drag) or keyboard-adjusting
            // (focused range). Any later caster_update — including the one their own commit triggers —
            // repaints once the interaction ends, so the value reconciles then.
            const active = document.activeElement;
            const busy = host.querySelector('input[type="range"]:active')
                || (active instanceof HTMLInputElement && active.type === 'range' && host.contains(active));
            if (busy) continue;
            // canEdit is read from the host, NOT recomputed: the creature card grants edit on the
            // modify-encounter tools page (hasPlayerPermission(blob) || ftIsModifyEncounterToolsPage()),
            // a predicate the PC card doesn't share — recomputing here would flip an editable slider to
            // a read-only bar on that page. The render sites stamp the decision they actually made.
            const canEdit = host.getAttribute('data-ft-canedit') === '1';
            const sideInfo = host.getAttribute('data-ft-sideinfo') === '1';
            replaceHydratedHtml(host, buildClassMechanicBars(blob, sideInfo, canEdit));
            try { playerWindow().ftMountIcons?.(host); } catch (e) { /* footer pass covers */ }
        }
    } catch (e) {
        console.warn('ftRerenderClassMechanicBars failed', e);
    }
}

// ── DISPATCHER — one entry for template.js: creature first (it gates PCs out), then PC ──────────
import { ftRenderCreatureCardFromCache, CreatureCardOpts } from './render-creature-card';

function ftRenderCasterCardFromCache(
    casterId: string,
    container: HTMLElement | string,
    opts: CreatureCardOpts & PlayerCardOpts = {},
): boolean {
    if (ftRenderCreatureCardFromCache(casterId, container, opts)) return true;
    return ftRenderPlayerCardFromCache(casterId, container, opts);
}

function ftRenderPcCoreAbilitiesFromCache(casterId: string, target: HTMLElement, sideInfo = false): boolean {
    const blob = recordOf(window.FateTide?.casterCache?.get(casterId));
    if (!blob) return false;
    // An unchanged block keeps its painted, hydrated DOM (rebuild audit 2026-10-25: every walk repainted it).
    paintMarkup(target, buildPcCoreAbilities(blob, casterId, sideInfo));
    return true;
}

window.addEventListener('ft:summons-changed', event => {
    for (const id of (event as CustomEvent<{ creatureIds: string[] }>).detail.creatureIds) ftRerenderSummonsStrip(id);
});
Object.assign(window, { ftRenderPcCoreAbilitiesFromCache, ftRenderPlayerCardFromCache, ftRenderCasterCardFromCache, ftRerenderSummonsStrip, ftRerenderClassMechanicBars });

export { ftRenderPlayerCardFromCache, ftRenderCasterCardFromCache, ftRerenderSummonsStrip, ftRerenderClassMechanicBars };
